import React, { useEffect, useRef, useState } from "react";
import { BsCaretRightFill } from "react-icons/bs";
import { animated, config, useSpring } from "react-spring";
import { bioPink, screenHues, screens } from "../../constants/constants";
import { useWindowSize } from "../../hooks/useWindowSize";
import { OnCurtainStart } from "../../types/types";

const RectangleButtonBorder = ({ offset, setRectangleHeight }) => {
  const ref = useRef<any>(null);
  const { currentDisplay } = { ...useWindowSize() };
  useEffect(() => {
    // rectangle height is just 12.5% of width. css 12.5% is of width
    setRectangleHeight(ref.current?.offsetHeight - 1);
  }, [currentDisplay, setRectangleHeight]);

  return (
    <div style={{ width: "100%", position: "absolute", display: "flex" }}>
      <div style={{ width: "50%" }} ref={ref}>
        <svg width="100%" viewBox="0 0 100 50">
          <animated.path
            transform="scale(-1, 1)"
            transform-origin="center"
            stroke={bioPink}
            fill="transparent"
            strokeWidth="3"
            pathLength="1"
            strokeDasharray="1"
            strokeDashoffset={offset}
            d="M0 0 h100 v50 h-100"
          />
        </svg>
      </div>
      <div style={{ width: "50%" }}>
        <svg width="100%" viewBox="0 0 100 50">
          <animated.path
            stroke={bioPink}
            fill="transparent"
            strokeWidth="3"
            pathLength="1"
            strokeDasharray="1"
            strokeDashoffset={offset}
            d="M0 0 h100 v50 h-100"
          />
        </svg>
      </div>
    </div>
  );
};

const RectangleButtonBorderBackground = ({
  rectangleStart,
  rectangleHeight,
  opacity,
}) => {
  return (
    <div style={{ width: "100%", position: "absolute" }}>
      <div style={{ width: "100%", height: rectangleHeight }}>
        <svg width="100%" height="100%">
          <animated.rect
            x={rectangleStart}
            width="100%"
            height="100%"
            fill={bioPink}
            opacity={opacity}
          />
        </svg>
      </div>
    </div>
  );
};

const RectangleTextAnimatedHeight = ({ height, text }) => {
  const { h } = useWindowSize();

  return (
    <div
      style={{
        // fontSize: "clamp(6px,1.2vh,24px)",
        fontSize: h(2),
        lineHeight: h(2),
        width: "100%",
        height: "100%",
        position: "absolute",
        display: "flex",
        fontWeight: "700",
        letterSpacing: "0.025em",
        alignItems: "center",
        zIndex: 10,
      }}
    >
      <div
        style={{
          // height: h(2),
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          width: "66.666667%",
        }}
      >
        <animated.div style={{ overflow: "hidden", height }}>
          {text}
        </animated.div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          width: "33.333333%",
        }}
      >
        <BsCaretRightFill />
      </div>
    </div>
  );
};

// for menu screen
const RectangleTextAnimatedWidth = ({ slideRightRevealText, text }) => {
  const { h } = useWindowSize();

  return (
    <div
      style={{
        fontSize: h(2),
        lineHeight: h(2),
        // fontSize: "clamp(6px,1.2vh,24px)",
        width: "100%",
        height: "100%",
        position: "absolute",
        display: "flex",
        zIndex: "10",
        fontWeight: "700",
        letterSpacing: "0.025em",
        alignItems: "center",
      }}
    >
      <animated.div
        {...{
          style: {
            display: "flex",
            width: "66.66%",
            justifyContent: "center",
            alignItems: "center",
            clipPath: slideRightRevealText,
          },
        }}
      >
        {text}
      </animated.div>
    </div>
  );
};

const RectangleButton = ({
  gridArea,
  text,
  width = "100%",
  screen = screens.landing,
  curtainTo,
  onCurtainStart,
  onClick,
  style,
}: {
  gridArea?: string;
  text: string;
  width?: string;
  screen?: string;
  curtainTo?: string;
  onCurtainStart?: OnCurtainStart;
  onClick?: Function;
  style?: React.CSSProperties;
}) => {
  const [rectangleHeight, setRectangleHeight] = useState(0);
  const [hover, setHover] = useState(false);
  const [click, setClick] = useState(false);

  const { h, horizontal } = useWindowSize();

  /* offset controls how react border appears */
  /* height controls how inner text appears */
  const { offset, height } = useSpring({
    from: { offset: 1, height: h(0) },
    to: { offset: 0, height: h(2) },
    config: {
      // duration: 4000,
      tension: 100,
      friction: 20,
      clamp: true,
    },
  });

  const { opacity } = useSpring({
    from: { opacity: 0.5 },
    to: { opacity: click ? 1 : 0.5 },
    config: {
      tension: 100,
      friction: 20,
      clamp: true,
    },
  });

  const { rectangleStart } = useSpring({
    from: { rectangleStart: hover ? "66.66%" : "100%" },
    rectangleStart: hover ? "0%" : "66.66%",
    config: config.molasses,
  });

  /// for width animation
  const { slideRightRevealText } = useSpring({
    /// inset top right bottome left
    /// from right side hidden
    from: { slideRightRevealText: "inset(0% 100% 0% 0%)" },
    /// to right side visible
    /// slides to the right
    to: { slideRightRevealText: "inset(0% 0% 0% 0%)" },
    // config: { duration: 6000 },
    config: config.molasses,
  });

  return (
    <div
      style={{
        gridArea,
        // placeSelf: horizontal ? "" : "end center",
        height: rectangleHeight,
        width: width ? width : "30%",
        cursor: "pointer",
        position: "relative",
        ...style,
      }}
      onMouseEnter={() => {
        setHover(true);
      }}
      onMouseLeave={() => {
        setHover(false);
      }}
      onClick={() => {
        // for opacity control
        setClick(!click);
        // for nav transition
        curtainTo && onCurtainStart?.({ curtainTo });
        onClick?.();
      }}
    >
      {screen === screens.menu && (
        <RectangleTextAnimatedWidth {...{ slideRightRevealText, text }} />
      )}
      {screen !== screens.menu && (
        <RectangleTextAnimatedHeight {...{ height, text }} />
      )}
      <RectangleButtonBorder {...{ offset, setRectangleHeight }} />
      <RectangleButtonBorderBackground
        {...{
          rectangleStart,
          rectangleHeight,
          opacity,
        }}
      />
    </div>
  );
};

export { RectangleButton };
